import type {
    RuleActionKey,
    ReportConfigurationState,
    ReportFolder,
    PlanConfig,
    ReportGlobalFilters,
    TripDataFieldState,
    TripDataFilterGroup,
    TripDataFilters,
    CallCenterSla,
    RuleCondition,
    RuleNode,
    RuleConditionGroup,
    CategorizationRule,
    RuleSet,
    OtpRule,
    ModelKey,
    MetricSection,
    ReportConfig,
    FilterGroupKey,
} from "../types";
import { TRIP_DATA_FIELDS, columnLabel, attachModel } from "./catalogResolution";
import type { ReportLogicContext } from "./context";

const RULE_ACTIONS: { key: RuleActionKey; label: string }[] = [
    { key: "set", label: "Set Value" },
    { key: "include", label: "Include Row" },
    { key: "exclude", label: "Exclude Row" },
];
const SLA_METRIC_DEFS = [
    { key: "answer", label: "Calls Answered Quickly", verb: "At least", mid: "of calls answered within", hasSeconds: true },
    { key: "secondary", label: "Calls Answered Very Quickly (Stretch Goal)", verb: "At least", mid: "of calls answered within", hasSeconds: true },
    { key: "abandoned", label: "Callers Who Hang Up", verb: "No more than", mid: "of calls should be abandoned", hasSeconds: false },
    { key: "hold", label: "Time on Hold", verb: "At least", mid: "of calls held under", hasSeconds: true },
];
const clone = <T,>(value: T): T => JSON.parse(JSON.stringify(value));
const makeEmptyConfigurationState = (): ReportConfigurationState => ({ plans: [], changes: [] });
const EMPTY_REPORT_FOLDER: ReportFolder = { id: "", label: "Reports", reports: [] };
const EMPTY_PLAN: PlanConfig = {
    id: "",
    name: "",
    clientName: "",
    reportFolders: [EMPTY_REPORT_FOLDER],
};

function makeReportGlobalFilters(source?: Partial<ReportGlobalFilters> | null): ReportGlobalFilters {
    return {
        tripActivity: source?.tripActivity || "active-only",
        exclusions: {
            memberIds: Array.isArray(source?.exclusions?.memberIds) ? [...source.exclusions.memberIds] : [],
            invoiceNumbers: Array.isArray(source?.exclusions?.invoiceNumbers) ? [...source.exclusions.invoiceNumbers] : [],
        },
    };
}

function defaultTripDataField(): TripDataFieldState {
    return { values: [], freeText: "", contains: "" };
}

function defaultTripDataFilterGroup(): TripDataFilterGroup {
    return {
        logic: "and",
        fields: TRIP_DATA_FIELDS.reduce((acc, field) => {
            acc[field.key] = defaultTripDataField();
            return acc;
        }, {} as Record<string, TripDataFieldState>),
    };
}

function defaultTripDataFilters(): TripDataFilters {
    return { include: defaultTripDataFilterGroup(), exclude: defaultTripDataFilterGroup() };
}

function defaultCallCenterSla(): CallCenterSla {
    return {
        active: false,
        ava: false,
        slaMetrics: {
            answer: { enabled: true, pct: 80, seconds: 30 },
            secondary: { enabled: false, pct: 90, seconds: 60 },
            abandoned: { enabled: true, pct: 5 },
            hold: { enabled: true, pct: 90, seconds: 210 },
        },
        countMode: "all",
        countDistinctColumn: "",
    };
}

function makeRuleCondition(context: ReportLogicContext): RuleCondition {
    return { id: context.nextId("cond"), type: "condition", column: "", operator: "in", values: [], text: "" };
}

function makeRuleConditionGroup(context: ReportLogicContext, logic: "and" | "or" = "or", children?: RuleNode[]): RuleConditionGroup {
    return {
        id: context.nextId("grp"),
        type: "group",
        conditionLogic: logic,
        conditions: children && children.length ? children : [makeRuleCondition(context)],
    };
}

function makeCategorizationRule(context: ReportLogicContext): CategorizationRule {
    return {
        id: context.nextId("rule"),
        label: "Rule",
        conditionLogic: "and",
        action: "set",
        targetField: "category",
        valueFromList: "",
        valueCustom: "",
        conditions: [makeRuleCondition(context)],
    };
}

function makeRuleSet(context: ReportLogicContext, name = "Categorization"): RuleSet {
    return { id: context.nextId("ruleset"), name, includeElse: true, elseLabel: "", rules: [makeCategorizationRule(context)] };
}

function makeOtpRule(context: ReportLogicContext): OtpRule {
    return { id: context.nextId("otp"), conditionLogic: "and", conditions: [], compareCol: "", againstCol: "", thresholdMinutes: 15 };
}

function makeMetric(context: ReportLogicContext, model: ModelKey | "" = "", column = "", name?: string): MetricSection {
    const label = name || (column ? `Count By ${columnLabel(context, column)}` : "");
    return {
        id: context.nextId("metric"),
        name: label,
        model,
        breakdownColumn: column,
        displayAs: "count",
        breakdownSource: "column",
        include: [],
        exclude: [],
        groups: [],
        extraBreakdowns: [],
        selectedColumns: [],
        filterColumn: "",
        filterValues: [],
        filterConditions: [],
        filterLogic: "and",
        excludeFilterColumn: "",
        excludeFilterValues: [],
        excludeFilterConditions: [],
        excludeFilterLogic: "or",
        coPlanClientIds: [],
        otpRules: [],
        otpMissingHandling: "exclude",
        noBreakdown: false,
        ruleSetId: "",
        nameCustom: !!name,
        showAdvanced: false,
        countMode: "all",
        countDistinctColumn: "",
    };
}

function makeReport(context: ReportLogicContext, id: string, label: string, models: ModelKey[], _seedMetrics = false): ReportConfig {
    const report: ReportConfig = {
        id,
        label,
        frequency: "Monthly",
        effectiveStart: context.today,
        effectiveEnd: "",
        models: [],
        dataModelIds: [],
        modelBasedOn: {},
        globalFilters: makeReportGlobalFilters(),
        reportingPeriod: { type: "PreviousMonth", customStart: "", customEnd: "" },
        bucketRuleSets: [],
        sections: [],
        tripDataFilters: defaultTripDataFilters(),
        callCenterSla: defaultCallCenterSla(),
        excluding: { items: [] },
        excelWorkbook: {
            titleTemplate: "",
            sheetHeaders: {},
            sheetOrder: [],
        },
    };

    models.forEach((model) => attachModel(context, report, model));
    if (models.includes("callcenter")) {
        report.callCenterSla.active = true;
    }
    return report;
}

function activeTripFilterCount(report: ReportConfig) {
    return (["include", "exclude"] as FilterGroupKey[]).reduce((sum, groupKey) => {
        return sum + TRIP_DATA_FIELDS.filter((field) => {
            const state = report.tripDataFilters[groupKey].fields[field.key];
            return state.values.length || state.freeText.trim() || state.contains.trim();
        }).length;
    }, 0);
}

function reportGlobalFilters(report: ReportConfig) {
    return makeReportGlobalFilters(report.globalFilters);
}

function findPlan(data: ReportConfigurationState, planId: string) {
    return data.plans.find((plan) => plan.id === planId) || data.plans[0] || EMPTY_PLAN;
}

function findFolder(plan: PlanConfig, folderId: string) {
    return plan.reportFolders.find((folder) => folder.id === folderId) || plan.reportFolders[0];
}

function findReport(folder?: ReportFolder, reportId?: string) {
    return folder?.reports.find((report) => report.id === reportId) || folder?.reports[0];
}

export {
    RULE_ACTIONS,
    SLA_METRIC_DEFS,
    clone,
    makeEmptyConfigurationState,
    EMPTY_REPORT_FOLDER,
    EMPTY_PLAN,
    makeReportGlobalFilters,
    defaultTripDataField,
    defaultTripDataFilterGroup,
    defaultTripDataFilters,
    defaultCallCenterSla,
    makeRuleCondition,
    makeRuleConditionGroup,
    makeCategorizationRule,
    makeRuleSet,
    makeOtpRule,
    makeMetric,
    makeReport,
    reportGlobalFilters,
    activeTripFilterCount,
    findPlan,
    findFolder,
    findReport,
};
